import { useState, useEffect, useRef } from 'react';

import { Coordinates, Dealer, DealersList } from '../typings/main';
import { getZipCodeFromCookies } from '../utils/cookies';
import { useGeocoding } from './use-geocoding';

interface UseMapInitializationProps {
  dealersList: DealersList[];
  defaultCenter: Coordinates;
  defaultZipCode: string;
  googleAPILoaded: boolean;
  isLocalDev?: boolean;
  userPreferredDealer?: Dealer;

  fetchDealersListByZipCode: (zipCode: string) => Promise<void>;
}

export const useMapInitialization = ({
  dealersList,
  defaultCenter,
  fetchDealersListByZipCode,
  googleAPILoaded,
  isLocalDev = false,
  userPreferredDealer,
  defaultZipCode
}: UseMapInitializationProps) => {
  const [hasInitialized, setHasInitialized] = useState<boolean>(false);
  const [isInitializing, setIsInitializing] = useState<boolean>(true);
  const [mapCenter, setMapCenter] = useState<Coordinates | null>(null);

  const isInitializingRef = useRef<boolean>(false);

  const { geocodeZipCode, getZipFromCoordinates } = useGeocoding();

  useEffect(() => {
    if (!googleAPILoaded || hasInitialized || isInitializingRef.current) return;

    isInitializingRef.current = true;

    const initializeMap = async () => {
      console.log('🗺️ Initializing map...');

      try {
        // Local dev - just show default center
        if (isLocalDev) {
          console.log('🧪 Local dev: Using default center without fetch');
          setMapCenter(defaultCenter);

          try {
            await fetchDealersListByZipCode(defaultZipCode);
          } catch (error) {
            console.error('Could not fetch dealers, in local dev:', error)
          }

          setIsInitializing(false);
          setHasInitialized(true);
          isInitializingRef.current = false;
          return;
        }

        // User preferred dealer ZIP if available
        if (userPreferredDealer?.address?.zip) {
          // console.log('⭐ Using preferred dealer ZIP:', userPreferredDealer.address.zip);
          const geocodedLocation = await geocodeZipCode(userPreferredDealer.address.zip);

          if (geocodedLocation) {
            setMapCenter(geocodedLocation);

            await fetchDealersListByZipCode(userPreferredDealer.address.zip);

            setIsInitializing(false);
            setHasInitialized(true);
            isInitializingRef.current = false;
            return;
          }
        }

        const cookieZip = getZipCodeFromCookies();

        // Has cookie ZIP
        if (cookieZip) {
          // console.log('🍪 Found cookie ZIP:', cookieZip);
          const geocodedLocation = await geocodeZipCode(cookieZip);

          if (geocodedLocation) {
            setMapCenter(geocodedLocation);

            await fetchDealersListByZipCode(cookieZip);

          } else {
            setMapCenter(defaultCenter);
          }
        } else {
          // No cookie - get default location ZIP
          // console.log('📍 No cookie, getting default location ZIP...');
          const defaultZip = await getZipFromCoordinates(defaultCenter.lat, defaultCenter.lng);

          if (defaultZip) {
            // console.log('✅ Default ZIP:', defaultZip);
            await fetchDealersListByZipCode(defaultZip);
          }

          setMapCenter(defaultCenter);
        }
      } catch (error) {
        console.error('❌ Error initializing map:', error);
        setMapCenter(defaultCenter);
      } finally {
        setIsInitializing(false);
        setHasInitialized(true);
        isInitializingRef.current = false;
      }
    };

    initializeMap();
  }, [googleAPILoaded]);


  // Update center when dealers change (for user searches)
  useEffect(() => {
    if (!hasInitialized || dealersList.length === 0) return;

    const updateCenterForNewDealers = async () => {
      console.log('🔄 Dealers changed, updating center...');

      // Get cookie ZIP (updated by search)
      const cookieZip = getZipCodeFromCookies();
      console.log('Cookie ZIP:', cookieZip);

      if (cookieZip) {
        const geocodedLocation = await geocodeZipCode(cookieZip);

        if (geocodedLocation) {
          console.log('✅ Geocoded ZIP to:', geocodedLocation);
          setMapCenter(geocodedLocation);
          return;
        }
      }

      // Fallback: use first dealer's location
      if (dealersList[0]?.location) {
        const firstDealerCenter = {
          lat: dealersList[0].location.latitude,
          lng: dealersList[0].location.longitude,
        };

        console.log('📍 Using first dealer location:', firstDealerCenter);
        setMapCenter(firstDealerCenter);
      }
    };

    updateCenterForNewDealers();
  }, [dealersList, hasInitialized, geocodeZipCode]);

  return {
    mapCenter,
    isInitializing
  };
};